/**
 * Toast — transient notifications.
 *
 * Mount `<ToastProvider>` near the root so any descendant can call
 * `useToast()` to enqueue a toast. Toasts auto-dismiss after `duration`
 * (default 6s) or when the user clicks the close button.
 *
 * Built on a small in-house provider rather than a third-party library —
 * keeps the bundle small and avoids pulling in a styling system that
 * fights Tailwind tokens.
 */

import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { cn } from "../../lib/cn";
import { Icon, type IconName } from "./Icon";
import { TapTarget } from "./TapTarget";

type ToastTone = "info" | "success" | "warning" | "danger";

export interface ShowToastInput {
  /** Stable id; passing the same id twice replaces the existing toast. */
  id?: string;
  tone?: ToastTone;
  title: string;
  description?: ReactNode;
  /** Optional CTA. The handler runs and the toast closes. */
  action?: { label: string; onClick: () => void };
  /** Milliseconds before auto-dismiss. Pass `null` to disable. Default 6000. */
  duration?: number | null;
  /** Accessible label for the close button. */
  dismissLabel: string;
}

interface ToastEntry extends ShowToastInput {
  id: string;
  tone: ToastTone;
}

interface ToastContextValue {
  show: (input: ShowToastInput) => string;
  dismiss: (id: string) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

let toastCounter = 0;
function nextId() {
  toastCounter += 1;
  return `toast-${toastCounter}`;
}

export interface ToastProviderProps {
  children: ReactNode;
}

export function ToastProvider({ children }: ToastProviderProps) {
  const [toasts, setToasts] = useState<ToastEntry[]>([]);

  const dismiss = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const show = useCallback((input: ShowToastInput) => {
    const id = input.id ?? nextId();
    setToasts((prev) => {
      const filtered = prev.filter((t) => t.id !== id);
      return [...filtered, { ...input, id, tone: input.tone ?? "info" }];
    });
    return id;
  }, []);

  const value = useMemo(() => ({ show, dismiss }), [show, dismiss]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <ToastViewport toasts={toasts} onDismiss={dismiss} />
    </ToastContext.Provider>
  );
}

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    throw new Error("useToast must be used within a <ToastProvider>");
  }
  return ctx;
}

const TONE_CLASSES: Record<ToastTone, { border: string; icon: string; iconName: IconName }> = {
  info: {
    border: "border-sky-300 dark:border-sky-700",
    icon: "text-sky-600 dark:text-sky-400",
    iconName: "info",
  },
  success: {
    border: "border-emerald-300 dark:border-emerald-700",
    icon: "text-emerald-600 dark:text-emerald-400",
    iconName: "check-circle",
  },
  warning: {
    border: "border-amber-300 dark:border-amber-700",
    icon: "text-amber-600 dark:text-amber-400",
    iconName: "alert-circle",
  },
  danger: {
    border: "border-rose-300 dark:border-rose-700",
    icon: "text-rose-600 dark:text-rose-400",
    iconName: "alert-circle",
  },
};

function ToastViewport({
  toasts,
  onDismiss,
}: {
  toasts: ToastEntry[];
  onDismiss: (id: string) => void;
}) {
  const containerRef = useRef<HTMLDivElement>(null);

  // `<dialog>`-elementer apnet med showModal() rendres i nettleserens top
  // layer og ligger over alt annet uavhengig av z-index. For at toasts skal
  // synes _over_ apne dialoger, flytter vi toast-containeren ogsa til top
  // layer via popover API (manual mode — ingen auto-dismiss/escape).
  // Faller stille tilbake til vanlig fixed-positioning hvis nettleseren
  // ikke stotter popover (eldre Safari/Firefox).
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    type PopoverElement = HTMLElement & { showPopover?: () => void };
    const popoverEl = el as PopoverElement;
    if (typeof popoverEl.showPopover !== "function") return;
    el.setAttribute("popover", "manual");
    try {
      popoverEl.showPopover();
    } catch {
      // Allerede synlig eller browser nekter — bruk fixed-positioning fallback.
    }
  }, []);

  if (toasts.length === 0) return null;
  return (
    <div
      ref={containerRef}
      aria-live="polite"
      aria-atomic="true"
      // Tilbakestill popover-default chrome (margin/padding/border/background)
      // sa min fixed-positioning og inline-toasts ser uforanderlige ut.
      //
      // Bottom-anchored on every viewport so a persistent toast (e.g. the
      // realtime "reconnecting" warning) doesn't cover the header or
      // primary CTAs on mobile. Mobile uses full-width centred cards;
      // desktop floats a compact card in the bottom-right corner.
      className="pointer-events-none fixed inset-x-0 bottom-4 z-50 m-0 flex max-h-none max-w-none flex-col items-center gap-2 overflow-visible border-0 bg-transparent p-0 px-4 sm:right-4 sm:bottom-4 sm:left-auto sm:items-end"
    >
      {toasts.map((toast) => (
        <ToastItem key={toast.id} toast={toast} onDismiss={onDismiss} />
      ))}
    </div>
  );
}

function ToastItem({ toast, onDismiss }: { toast: ToastEntry; onDismiss: (id: string) => void }) {
  useEffect(() => {
    if (toast.duration === null) return;
    const ms = toast.duration ?? 6000;
    const timer = window.setTimeout(() => onDismiss(toast.id), ms);
    return () => window.clearTimeout(timer);
  }, [toast.id, toast.duration, onDismiss]);

  const tone = TONE_CLASSES[toast.tone];

  return (
    <output
      className={cn(
        "pointer-events-auto block w-full max-w-sm animate-scale-in rounded-lg border bg-surface shadow-popover",
        tone.border,
      )}
    >
      <div className="flex items-start gap-3 p-3">
        <Icon name={tone.iconName} size="sm" className={cn("mt-0.5 shrink-0", tone.icon)} />
        <div className="min-w-0 flex-1">
          <div className="font-medium text-ink-900 text-sm">{toast.title}</div>
          {toast.description && (
            <div className="mt-1 text-ink-600 text-xs">{toast.description}</div>
          )}
          {toast.action && (
            <div className="mt-2">
              <TapTarget
                onClick={() => {
                  toast.action?.onClick();
                  onDismiss(toast.id);
                }}
                className="font-medium text-accent-700 text-xs underline-offset-4 hover:text-accent-900 hover:underline dark:text-accent-300 dark:hover:text-accent-100"
              >
                {toast.action.label}
              </TapTarget>
            </div>
          )}
        </div>
        <TapTarget
          onClick={() => onDismiss(toast.id)}
          aria-label={toast.dismissLabel}
          className="shrink-0 rounded p-1 text-ink-400 hover:bg-ink-100 hover:text-ink-700"
        >
          <Icon name="x" size="xs" />
        </TapTarget>
      </div>
    </output>
  );
}
